import React, { useState, useEffect } from "react";
import { message, Modal } from "antd";
import {
  RiBaseStationFill,
  RiLink,
  RiSoundModuleFill,
  RiTruckFill,
  RiHistoryLine,
} from "react-icons/ri";
import {
  ReTryIcon,
  FloorPlanIcon,
  PersonIcon,
  MqttIcon,
  DashboardIcon,
} from "../Icons";
import {
  FloorPlanModal,
  BaseStationModal,
  PersonnelModal,
  MqttModal,
  LiveDashboardModal,
  HistoryWorkspaceModal,
  AlertConfigModal,
  EquipmentModal,
} from "../modal";
import IndoorPositioningLive from "../../api/IndoorPositioningLive";
import IndoorPositioningStations from "../../api/IndoorPositioningStations";
import { useGlobal } from "../../global/user";
import "./function.css";

export default function Function4({ machineID, data, status, machineInfo }) {
  const { user } = useGlobal();
  const [state, setState] = useState({
    onDutyCount: 0,
    baseStationCount: 0,
    connectionStatus: "載入中",
  });

  const [loading, setLoading] = useState(false);

  const fetchStats = async () => {
    if (!machineID) return;

    try {
      const [personnelResponse, stationsResponse] = await Promise.all([
        IndoorPositioningLive.GetPersonnel(machineID),
        IndoorPositioningStations.GetStations(machineID),
      ]);

      const newState = {
        onDutyCount: 0,
        baseStationCount: 0,
        connectionStatus: "離線",
      };

      if (personnelResponse.code === 200) {
        const personnelData = personnelResponse.data?.personnel || [];
        const stats = personnelResponse.data?.stats;

        if (stats && stats.onDutyPersonnel !== undefined) {
          newState.onDutyCount = stats.onDutyPersonnel;
        } else if (Array.isArray(personnelData)) {
          const onDutyPersonnel = personnelData.filter(
            (p) => p.onDuty === 1 || p.onDuty === true,
          );
          newState.onDutyCount = onDutyPersonnel.length;
        }

        newState.connectionStatus = "在線";
      }

      if (
        stationsResponse.code === 200 &&
        Array.isArray(stationsResponse.data)
      ) {
        newState.baseStationCount = stationsResponse.data.length;
      }

      setState(newState);
    } catch (error) {
      console.error("Failed to fetch indoor positioning stats:", error);
      setState((prev) => ({ ...prev, connectionStatus: "離線" }));
    }
  };

  useEffect(() => {
    fetchStats();

    const interval = setInterval(fetchStats, 10000);

    return () => clearInterval(interval);
  }, [machineID]);

  // 各個 Modal 的開關狀態
  const [floorPlanOpen, setFloorPlanOpen] = useState(false);
  const [baseStationOpen, setBaseStationOpen] = useState(false);
  const [personnelOpen, setPersonnelOpen] = useState(false);
  const [equipmentOpen, setEquipmentOpen] = useState(false);
  const [mqttOpen, setMqttOpen] = useState(false);
  const [liveDashboardOpen, setLiveDashboardOpen] = useState(false);
  const [historyWorkspaceOpen, setHistoryWorkspaceOpen] = useState(false);
  const [alertConfigOpen, setAlertConfigOpen] = useState(false);

  const { IP, port, name } = machineInfo || {};

  const ReloadData = async () => {
    try {
      setLoading(true);
      message.loading("正在刷新數據...", 1);

      await fetchStats();

      message.success("刷新成功");
    } catch (error) {
      message.error("刷新失敗");
      console.error("Reload error:", error);
    } finally {
      setLoading(false);
    }
  };

  const ReloadConfirm = () => {
    Modal.confirm({
      title: "確認刷新",
      content: `確認刷新室內定位系統 ${name || machineID} 的數據？`,
      onOk: ReloadData,
    });
  };

  const copyPublicDashboardUrl = async () => {
    const publicUrl = `${window.location.origin}${window.location.pathname}#/public/live-dashboard/${machineID}`;

    try {
      await navigator.clipboard.writeText(publicUrl);
      message.success("監控面板連結已複製到剪貼簿");
    } catch (error) {
      const textArea = document.createElement("textarea");
      textArea.value = publicUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        message.success("監控面板連結已複製到剪貼簿");
      } catch (err) {
        message.error("複製失敗，請手動複製");
      }

      document.body.removeChild(textArea);
    }
  };

  return (
    <React.Fragment>
      <td>
        <div className="d-flex justify-content-center py-2">
          <div style={{ width: "80%" }}>
            {/* 狀態顯示區 */}
            <div className="d-flex justify-content-between mb-3">
              <div>
                <span className="font-weight-bold mr-1">編號：</span>
                <span className="mr-2 text-nowrap">{machineID}</span>
              </div>
              <div>
                <span className="font-weight-bold mr-1">值班人數：</span>
                <span className="mr-2 text-nowrap">{state.onDutyCount} 人</span>
              </div>
              <div>
                <span className="font-weight-bold mr-1">基站數量：</span>
                <span className="mr-2 text-nowrap">
                  {state.baseStationCount} 站
                </span>
              </div>
            </div>

            {/* 控制按鈕區 */}
            <div className="d-flex justify-content-center align-items-center flex-wrap">
              {/* <button
                className="control_setting m-1"
                onClick={() => setLiveDashboardOpen(true)}
                title="即時監控"
              >
                <DashboardIcon className="button_svg control_svg" />
              </button> */}

              <button
                className="control_setting m-1"
                onClick={copyPublicDashboardUrl}
                title="複製監控面板連結"
              >
                <RiLink className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={() => setHistoryWorkspaceOpen(true)}
                title="歷史資料"
              >
                <RiHistoryLine className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={() => setFloorPlanOpen(true)}
                title="平面圖"
              >
                <FloorPlanIcon className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={() => setBaseStationOpen(true)}
                title="基站資料"
              >
                <RiBaseStationFill className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={() => setPersonnelOpen(true)}
                title="人員資料"
              >
                <PersonIcon className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={() => setEquipmentOpen(true)}
                title="機具資料"
              >
                <RiTruckFill className="button_svg control_svg" />
              </button>

              {user.class >= process.env.REACT_APP_EDITOR_CALSS_LV && (
                <button
                  className="control_setting m-1"
                  onClick={() => setMqttOpen(true)}
                  title="MQTT設定"
                >
                  <MqttIcon className="button_svg control_svg" />
                </button>
              )}

              <button
                className="control_setting m-1"
                onClick={() => setAlertConfigOpen(true)}
                title="警報設定"
              >
                <RiSoundModuleFill className="button_svg control_svg" />
              </button>

              <button
                className="control_setting m-1"
                onClick={ReloadConfirm}
                title="刷新數據"
              >
                <ReTryIcon className="button_svg control_svg" />
              </button>
            </div>
          </div>
        </div>

        {/* 所有 Modal */}
        <LiveDashboardModal
          open={liveDashboardOpen}
          onClose={() => setLiveDashboardOpen(false)}
          machineID={machineID}
        />

        <HistoryWorkspaceModal
          open={historyWorkspaceOpen}
          onClose={() => setHistoryWorkspaceOpen(false)}
          machineID={machineID}
        />

        <FloorPlanModal
          open={floorPlanOpen}
          onClose={() => setFloorPlanOpen(false)}
          machineID={machineID}
        />

        <BaseStationModal
          open={baseStationOpen}
          onClose={() => setBaseStationOpen(false)}
          machineID={machineID}
        />

        <PersonnelModal
          open={personnelOpen}
          onClose={() => setPersonnelOpen(false)}
          machineID={machineID}
        />

        <EquipmentModal
          open={equipmentOpen}
          onClose={() => setEquipmentOpen(false)}
          machineID={machineID}
        />

        <MqttModal
          open={mqttOpen}
          onClose={() => setMqttOpen(false)}
          machineID={machineID}
        />

        <AlertConfigModal
          open={alertConfigOpen}
          onClose={() => setAlertConfigOpen(false)}
          machineID={machineID}
        />
      </td>
    </React.Fragment>
  );
}
